// AF-3.18 — Data Connections. Read-only connector foundation UI.
//
// CRITICAL INVARIANT (spec Section 13): this page NEVER shows "Healthy"
// merely because a credential reference was created or OAuth notionally
// succeeded -- the connector's status/last_sync_status are the backend's
// OWN enum values, rendered verbatim through StatusBadge (never inferred
// client-side), and a live health check is a real read-only API call the
// user explicitly triggers, not a client-side assumption.
//
// No publish/mutate action of any kind exists on this page -- only
// Connect / Health Check / Sync / Disconnect, matching the connector
// abstraction's own read-only capability set.
//
// Milestone 4 (Commerce Intelligence): ConnectDialog now actually asks
// for and stores a real secret (vaultAPI.save, the same real vault
// every other provider key already uses) -- previously it only ever
// created a CredentialReference and no real credential could ever be
// stored anywhere, so no sync could ever succeed. This page was already
// unlocked for free-plan brands (DashboardLayout.js) before this
// change; this closes the one real gap in an otherwise fully-wired
// connector foundation.

import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { BrandContextBar } from '@/components/brand/BrandContextBar';
import { StatusBadge } from '@/components/brand/Badges';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useBrandContext } from '@/hooks/useBrandContext';
import { connectorsAPI, credentialReferencesAPI } from '@/lib/intelligenceApi';
import { vaultAPI } from '@/lib/api';
import { toast } from 'sonner';

// Milestone 20 (Paid Growth / Google Ads Observe -> Find): "PAID GROWTH ·
// GOOGLE" label follows the exact same subtle provenance pattern as
// Meta's own "Meta Ads" label -- no new visual language introduced.
const PROVIDER_LABELS = { shopify: 'Shopify', meta_marketing: 'Meta Ads', zernio: 'Zernio (Social)', google_ads: 'Google Ads' };
// Milestone 4 (Commerce Intelligence): the real, actual gap this closes --
// ConnectDialog previously only ever created a CredentialReference
// (metadata) and never asked for or stored a real secret anywhere, so no
// sync could ever succeed. /vault/save (vaultAPI.save) is the SAME real,
// already-existing vault endpoint VaultPage.js uses for every other
// provider key -- this is the one missing UI path, not a new backend
// capability. client_id is passed explicitly (not left to default to the
// tenant id) so it matches exactly what server.py's
// _resolve_connector_secret looks up at sync time: (tenant_id, key_name,
// THIS workspace's client_id).
const PROVIDER_SECRET_LABEL = { shopify: 'Admin API access token', meta_marketing: 'Access token', zernio: 'Zernio API key' };

// Milestone 5 (Social Organic Agent): Zernio's own "profileId" groups
// connected social accounts together -- the real scoping concept, per
// docs.zernio.com. Required (never left empty), so one Zernio API key
// shared across multiple Martian workspaces can never leak one brand's
// connected accounts into another's sync (see server.py's
// _build_connector_instance).
const PROVIDER_ACCOUNT_LABEL = { shopify: 'Shop domain', meta_marketing: 'Ad account ID', zernio: 'Zernio profile ID', google_ads: 'Customer ID' };
const PROVIDER_ACCOUNT_PLACEHOLDER = { shopify: 'acme.myshopify.com', meta_marketing: '123456789', zernio: 'profile_...', google_ads: '123-456-7890' };

// Milestone 20 -- Google's real OAuth model needs FOUR values
// simultaneously (client_id, client_secret, refresh_token,
// developer_token) plus an optional fifth (login_customer_id, only for a
// manager/MCC account acting on behalf of this Customer ID) to make even
// one authenticated call -- confirmed against Google's own "Authorization
// and HTTP headers" developer docs during this milestone's audit. Every
// other provider on this page needs exactly one secret string, which is
// all vaultAPI.save's key_value has ever stored -- rather than inventing
// a second secret-storage architecture, these four/five fields are
// JSON-encoded into that SAME single string (see connectors/google_ads.py's
// own module docstring for the read side of this).
const GOOGLE_CREDENTIAL_FIELDS = [
  { key: 'client_id', label: 'OAuth Client ID', placeholder: '....apps.googleusercontent.com', required: true },
  { key: 'client_secret', label: 'OAuth Client Secret', placeholder: 'GOCSPX-...', required: true, secret: true },
  { key: 'refresh_token', label: 'Refresh Token', placeholder: '1//0g...', required: true, secret: true },
  { key: 'developer_token', label: 'Developer Token', placeholder: 'From ads.google.com/aw/apicenter', required: true, secret: true },
  { key: 'login_customer_id', label: 'Manager (MCC) Customer ID', placeholder: '123-456-7890 (optional)', required: false },
];

function SyncSummary({ connector }) {
  if (!connector.last_sync_at) {
    return <span className="text-[10px] text-[var(--text-tertiary)]">Never synced</span>;
  }
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-mono text-[var(--text-secondary)]">{new Date(connector.last_sync_at).toLocaleString()}</span>
      <StatusBadge value={connector.last_sync_status} />
    </div>
  );
}

function ConnectDialog({ open, onOpenChange, workspaceId, onConnected }) {
  const [provider, setProvider] = useState('shopify');
  const [accountReference, setAccountReference] = useState('');
  const [secretValue, setSecretValue] = useState('');
  // Milestone 20 -- Google's own multi-field OAuth credential, kept as a
  // separate piece of state (never merged into secretValue until submit)
  // so this page's existing single-secret providers are untouched.
  const [googleFields, setGoogleFields] = useState({ client_id: '', client_secret: '', refresh_token: '', developer_token: '', login_customer_id: '' });
  const [submitting, setSubmitting] = useState(false);

  const isGoogle = provider === 'google_ads';

  const submit = async () => {
    // Milestone 5 fix: account_reference is a real, optional FILTER for
    // Zernio (its own /v1/accounts endpoint treats profileId as
    // optional -- omit it to read every account under the API key), not
    // a required identifier the way Shopify's shop domain is. Requiring
    // a value here for every provider caused a real live bug: a
    // reasonable-looking placeholder string ("All Profiles") got sent
    // as a literal profileId and Zernio correctly rejected it as an
    // invalid ObjectId. Only Shopify/Meta Ads/Google Ads still require one.
    if (provider !== 'zernio' && !accountReference.trim()) {
      toast.error(`${PROVIDER_ACCOUNT_LABEL[provider] || 'Account reference'} is required`);
      return;
    }
    let resolvedSecretValue = secretValue;
    if (isGoogle) {
      const missing = GOOGLE_CREDENTIAL_FIELDS.filter((f) => f.required && !googleFields[f.key].trim());
      if (missing.length > 0) {
        toast.error(`${missing[0].label} is required`);
        return;
      }
      // The one place these fields are combined into the single string
      // every other provider's key_value already is -- never sent
      // separately, never logged. login_customer_id is omitted entirely
      // (not sent as an empty string) when left blank, matching
      // GoogleAdsConnector's own creds.get('login_customer_id') read.
      resolvedSecretValue = JSON.stringify({
        client_id: googleFields.client_id.trim(),
        client_secret: googleFields.client_secret.trim(),
        refresh_token: googleFields.refresh_token.trim(),
        developer_token: googleFields.developer_token.trim(),
        ...(googleFields.login_customer_id.trim() ? { login_customer_id: googleFields.login_customer_id.trim().replace(/-/g, '') } : {}),
      });
    } else if (!secretValue.trim()) {
      toast.error(`${PROVIDER_SECRET_LABEL[provider] || 'Access token'} is required`);
      return;
    }
    setSubmitting(true);
    try {
      // Step 1: store the real secret in the existing encrypted vault --
      // scoped to THIS workspace so the sync route's own credential
      // lookup finds it later. Never sent anywhere else, never logged.
      await vaultAPI.save({ key_name: provider, key_value: resolvedSecretValue.trim(), client_id: workspaceId });

      // Step 2: create (or reuse) a CredentialReference -- metadata only,
      // never a raw secret (AF-3.17.1's own service rejects one; the
      // real secret from step 1 is what makes its status resolve to
      // CONNECTED).
      const credRes = await credentialReferencesAPI.create(workspaceId, {
        provider, credential_type: 'OAUTH_TOKEN',
      });
      const credentialReferenceId = credRes.data.id;

      const connectorRes = await connectorsAPI.create(workspaceId, {
        provider, credential_reference_id: credentialReferenceId,
        // Sent as undefined (never an empty/placeholder string) when
        // left blank -- ZernioConnector.sync() already correctly omits
        // the profileId filter entirely when falsy.
        account_reference: accountReference.trim() || undefined, display_name: PROVIDER_LABELS[provider],
      });
      toast.success(`${PROVIDER_LABELS[provider]} connected`);
      onConnected(connectorRes.data);
      setAccountReference('');
      setSecretValue('');
      setGoogleFields({ client_id: '', client_secret: '', refresh_token: '', developer_token: '', login_customer_id: '' });
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to connect');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="connect-dialog">
        <DialogHeader>
          <DialogTitle>Connect a data source</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Provider</Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger data-testid="connect-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="shopify">Shopify</SelectItem>
                <SelectItem value="meta_marketing">Meta Ads</SelectItem>
                <SelectItem value="zernio">Zernio (Social)</SelectItem>
                <SelectItem value="google_ads">Google Ads</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{PROVIDER_ACCOUNT_LABEL[provider] || 'Account reference'}{provider === 'zernio' && <span className="text-[var(--text-tertiary)] font-normal"> (optional)</span>}</Label>
            <Input
              data-testid="connect-account-reference-input"
              placeholder={PROVIDER_ACCOUNT_PLACEHOLDER[provider] || ''}
              value={accountReference}
              onChange={(e) => setAccountReference(e.target.value)}
            />
            {provider === 'zernio' && (
              <p className="text-[10px] text-[var(--text-tertiary)]">
                Leave blank to read every account under this API key. Only set this if you use Zernio Profiles to separate multiple brands under one key.
              </p>
            )}
          </div>
          {isGoogle ? (
            <div className="space-y-3" data-testid="google-credential-fields">
              {GOOGLE_CREDENTIAL_FIELDS.map((f) => (
                <div className="space-y-1.5" key={f.key}>
                  <Label>{f.label}{!f.required && <span className="text-[var(--text-tertiary)] font-normal"> (optional)</span>}</Label>
                  <Input
                    type={f.secret ? 'password' : 'text'}
                    data-testid={`connect-google-${f.key}-input`}
                    placeholder={f.placeholder}
                    value={googleFields[f.key]}
                    onChange={(e) => setGoogleFields((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  />
                </div>
              ))}
              <p className="text-[10px] text-[var(--text-tertiary)]">
                From a Google Ads API access application (ads.google.com/aw/apicenter) and an OAuth2 client with the
                https://www.googleapis.com/auth/adwords scope. Only set Manager (MCC) Customer ID if you access this
                account through a manager account. Martian only reads campaign performance — it never changes budgets,
                bids, keywords, audiences, or ad copy, and never writes to your Google Ads account.
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>{PROVIDER_SECRET_LABEL[provider] || 'Access token'}</Label>
              <Input
                type="password"
                data-testid="connect-secret-input"
                placeholder={provider === 'shopify' ? 'shpat_...' : 'Paste your access token'}
                value={secretValue}
                onChange={(e) => setSecretValue(e.target.value)}
              />
              {provider === 'shopify' && (
                <p className="text-[10px] text-[var(--text-tertiary)]">
                  From a custom app in your Shopify admin (Settings → Apps → Develop apps), with read-only access to products and orders.
                </p>
              )}
              {provider === 'zernio' && (
                <p className="text-[10px] text-[var(--text-tertiary)]">
                  From your Zernio dashboard (Settings → API Keys). Martian only reads connected account and post data — it never publishes, schedules, or sends anything through Zernio.
                </p>
              )}
            </div>
          )}
          <p className="text-[10px] text-[var(--text-tertiary)]">
            This creates a read-only connection record. Martian requests only product, inventory, order, and
            spend/performance data -- never customer names, emails, phone numbers, or addresses. No publishing
            or account-mutation capability is ever granted. Your access token is stored encrypted and is never
            displayed again after you connect.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="connect-submit-button" onClick={submit} disabled={submitting}>
            {submitting ? 'Connecting…' : 'Connect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DataConnectionsPage() {
  const ctx = useBrandContext();
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [lastSyncSummary, setLastSyncSummary] = useState(null);

  const load = useCallback(() => {
    if (!ctx.workspaceId) return;
    setLoading(true); setError(null);
    connectorsAPI.list(ctx.workspaceId)
      .then((r) => setConnectors(r.data?.connectors || []))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [ctx.workspaceId]);

  useEffect(() => { load(); }, [load]);

  const runHealthCheck = async (connectorId) => {
    setBusyId(connectorId);
    try {
      const res = await connectorsAPI.healthCheck(ctx.workspaceId, connectorId);
      const status = res.data?.health?.status;
      if (status === 'OK') toast.success('Health check passed');
      else toast.error(res.data?.health?.error_message_safe || 'Health check failed');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Health check failed');
    } finally {
      setBusyId(null);
    }
  };

  const runSync = async (connectorId) => {
    setBusyId(connectorId);
    try {
      const res = await connectorsAPI.sync(ctx.workspaceId, connectorId, {});
      setLastSyncSummary(res.data);
      const status = res.data?.sync_result?.status;
      if (status === 'SUCCESS') toast.success(`Sync complete — ${res.data.sync_result.records_retrieved} records retrieved`);
      else if (status === 'PARTIAL') toast.warning('Sync partially completed — see warnings');
      else toast.error('Sync failed');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setBusyId(null);
    }
  };

  const disconnect = async (connectorId) => {
    setBusyId(connectorId);
    try {
      await connectorsAPI.disconnect(ctx.workspaceId, connectorId);
      toast.success('Disconnected');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <DashboardLayout>
      <div data-testid="data-connections-page" className="max-w-5xl mx-auto">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Data Connections</h1>
            <p className="text-[12px] text-[var(--text-secondary)]">
              Read-only connections to commerce and paid-media sources. Martian can never publish, post, or
              change budgets/bids/audiences through any connection here.
            </p>
          </div>
          <Button data-testid="open-connect-dialog-button" onClick={() => setDialogOpen(true)}>Connect a source</Button>
        </div>

        <BrandContextBar ctx={ctx} />

        {loading && <LoadingState />}
        {error && <ErrorState error={error} onRetry={load} />}

        {!loading && !error && connectors.length === 0 && (
          <EmptyState
            title="NO DATA SOURCES CONNECTED"
            description="Connect Shopify or a paid-media account to bring real brand data into Martian's intelligence."
            actionLabel="Connect a source"
            onAction={() => setDialogOpen(true)}
          />
        )}

        {!loading && !error && connectors.length > 0 && (
          <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-card)] overflow-x-auto" data-testid="connectors-table">
            <Table>
              <TableHeader>
                <TableRow className="border-[var(--border)] hover:bg-transparent">
                  <TableHead className="text-[10px] text-[var(--text-tertiary)]">Source</TableHead>
                  <TableHead className="text-[10px] text-[var(--text-tertiary)]">Status</TableHead>
                  <TableHead className="text-[10px] text-[var(--text-tertiary)]">Last Sync</TableHead>
                  <TableHead className="text-[10px] text-[var(--text-tertiary)]">Coverage</TableHead>
                  <TableHead className="text-[10px] text-[var(--text-tertiary)]">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {connectors.map((c) => (
                  <TableRow key={c.connector_id} className="border-[var(--border)]" data-testid="connector-row">
                    <TableCell className="text-[11px] text-[var(--text-primary)]">
                      {c.display_name || PROVIDER_LABELS[c.provider] || c.provider}
                      <div className="text-[10px] text-[var(--text-tertiary)] font-mono">{c.account_reference || '—'}</div>
                    </TableCell>
                    <TableCell><StatusBadge value={c.status} /></TableCell>
                    <TableCell><SyncSummary connector={c} /></TableCell>
                    <TableCell className="text-[10px] text-[var(--text-secondary)]">
                      {(c.capabilities?.read || []).length > 0 ? `${c.capabilities.read.length} read scope(s)` : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" disabled={busyId === c.connector_id} onClick={() => runHealthCheck(c.connector_id)} data-testid="health-check-button">
                          Health check
                        </Button>
                        <Button size="sm" variant="ghost" disabled={busyId === c.connector_id} onClick={() => runSync(c.connector_id)} data-testid="sync-button">
                          Sync
                        </Button>
                        <Button size="sm" variant="ghost" disabled={busyId === c.connector_id} onClick={() => disconnect(c.connector_id)} data-testid="disconnect-button">
                          Disconnect
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {lastSyncSummary && (
          <div className="mt-4 rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-card)] p-4" data-testid="last-sync-summary">
            <h2 className="text-[12px] font-semibold text-[var(--text-primary)] mb-2">Last sync result</h2>
            <div className="grid grid-cols-2 gap-2 text-[11px] text-[var(--text-secondary)]">
              <div>Records retrieved: <span className="text-[var(--text-primary)] font-mono">{lastSyncSummary.sync_result?.records_retrieved ?? 0}</span></div>
              <div>Measurements created: <span className="text-[var(--text-primary)] font-mono">{lastSyncSummary.measurements_created ?? 0}</span></div>
              <div>Status: <StatusBadge value={lastSyncSummary.sync_result?.status} /></div>
              <div>Next cursor: <span className="text-[var(--text-primary)] font-mono">{lastSyncSummary.sync_result?.cursor ? 'more available' : 'none'}</span></div>
            </div>
            {(lastSyncSummary.sync_result?.warnings || []).length > 0 && (
              <div className="mt-2">
                <p className="text-[10px] text-[var(--warning)] font-semibold uppercase">Sync warnings</p>
                <ul className="text-[10px] text-[var(--text-secondary)] list-disc list-inside">
                  {lastSyncSummary.sync_result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <ConnectDialog open={dialogOpen} onOpenChange={setDialogOpen} workspaceId={ctx.workspaceId} onConnected={load} />
      </div>
    </DashboardLayout>
  );
}
